import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { SectionLabel } from './ui/UI';
import { DecryptedText } from './animations/DecryptedText';
/**
 * Competitive — why Periscan vs. scanners / pentests / dashboards.
 *
 * The DecryptedText "reveal" beat on the 4 subjects is the section's
 * memorable moment — kept as-is. What changed: the 6 differentiator cards
 * underneath were the same icon+headline+2-lines AI feature-grid pattern.
 * Replaced with an editorial numbered list — same content, no chrome.
 */
const reasons = [{
  title: 'Continuous, not calendar-based.',
  body: 'Security posture changes constantly. Validation should not wait for audit season.'
}, {
  title: 'Evidence, not assumptions.',
  body: 'Periscan shows what your controls did, what they missed, and what needs attention.'
}, {
  title: 'Built for operators.',
  body: 'Results should help teams fix things — not admire the problem in a prettier chart.'
}, {
  title: 'Useful for leaders.',
  body: 'Track control effectiveness, remediation progress, and risk reduction in language executives can understand.'
}, {
  title: 'Designed for repeatability.',
  body: 'Run validation again and again, so improvement becomes measurable instead of anecdotal.'
}, {
  title: 'Focused on what matters.',
  body: 'The goal is not to produce more findings. The goal is to prove what needs fixing first.'
}];
export function Competitive() {
  return <section id="why-periscan" className="relative w-full py-32 border-t border-white/5" data-id="element-306">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-307">
        <SectionLabel number="—" label="Why Periscan" className="mb-8" data-id="element-308" />

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16" data-id="element-309">
          <div className="lg:col-span-12" data-id="element-310">
            <motion.h2 initial={{
            opacity: 0,
            y: 16
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-50px'
          }} transition={{
            duration: 0.6
          }} className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold tracking-[-0.02em] leading-[1.05] text-white" data-id="element-311">
              Not a scanner. Not a pentest replacement.
              <br data-id="element-312" />
              <span className="text-white/40" data-id="element-313">
                Not another dashboard with commitment issues.
              </span>
            </motion.h2>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-24" data-id="element-314">
          <div className="lg:col-span-8 space-y-3" data-id="element-315">
            <p className="text-lg text-white/70 leading-relaxed" data-id="element-316">
              <DecryptedText text="Vulnerability scanners" speed={28} className="text-white font-sans" data-id="element-317" />{' '}
              show what might be wrong.
            </p>
            <p className="text-lg text-white/70 leading-relaxed" data-id="element-318">
              <DecryptedText text="Penetration tests" speed={28} className="text-white font-sans" data-id="element-319" />{' '}
              show what skilled humans found during a point-in-time assessment.
            </p>
            <p className="text-lg text-white/70 leading-relaxed" data-id="element-320">
              <DecryptedText text="Dashboards" speed={28} className="text-white font-sans" data-id="element-321" />{' '}
              show what your tools report.
            </p>
            <p className="text-lg text-white leading-relaxed pt-2" data-id="element-322">
              <DecryptedText text="Periscan" speed={32} className="text-[#1a8fd4] font-sans" data-id="element-323" />{' '}
              validates what actually happens when attacker behavior meets your
              defenses — continuously, repeatedly, and with evidence your team
              can use.
            </p>
          </div>
        </div>

        {/* Six reasons — editorial list, no icons, no card chrome */}
        <ol className="border-t border-white/5" data-id="element-324">
          {reasons.map((r, i) => <motion.li key={r.title} initial={{
          opacity: 0,
          y: 12
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-40px'
        }} transition={{
          duration: 0.5,
          delay: i * 0.04
        }} className="border-b border-white/5 py-9 grid grid-cols-12 gap-6 items-baseline" data-id="element-325">
              <span className="col-span-12 md:col-span-1 font-mono text-sm text-[#1a8fd4]/70 tracking-[0.2em]" data-id="element-326">
                {String(i + 1).padStart(2, '0')}
              </span>
              <h3 className="col-span-12 md:col-span-4 text-2xl md:text-3xl text-white font-semibold tracking-tight leading-snug" data-id="element-327">
                {r.title}
              </h3>
              <p className="col-span-12 md:col-span-7 text-base md:text-lg text-white/65 leading-relaxed max-w-2xl" data-id="element-328">
                {r.body}
              </p>
            </motion.li>)}
        </ol>
      </div>
    </section>;
}